import * as API from '@/API';
import {
  joinSession as playerJoinSession,
  submitDrawing as playerSubmitDrawing,
  reactToArtwork as playerReactToArtwork,
  updateQuestion,
  updateSentiment,
  getReactions
} from '@/client/player';
import { PlayerSessionContext } from '@/contexts/PlayerSessionContext';
import { Reaction as React, reactionTypeForNumber } from '@/utils/reactions';
import { Reaction } from '@/components/Controls/ReactionsBar';
import { gql, useQuery, useSubscription } from '@apollo/client';
import { GraphQLQuery, GraphQLSubscription } from 'aws-amplify/api';
import { ReactNode, useEffect, useState } from 'react';
import { getCookie, setCookie } from 'typescript-cookie';
import { sessionQuery } from './HostSessionControlProvider';

const playerSubscription = gql`
  subscription PlayerSessionUpdated($id: ID!) {
    playerSessionUpdated(id: $id) {
      __typename
      ... on NewRoundUpdate {
        round
        sessionStatus
        playerStatus
      }
      ... on SharingPlayer {
        playerId
      }
      ... on NewDrawingUpdate {
        artwork {
          drawing
          id
          playerId
        }
      }
      ... on NewPlayerUpdate {
        player {
          id
          name
          gameStatus
          connectionStatus
          feelingBefore
          feelingAfter
          question
          questionAnswer
        }
      }
      ... on SessionStatusUpdate {
        status
      }
    }
  }
`;

export const artworkReactionsQuery = gql`
  query PlayerArtworkReactions($sessionID: ID!) {
    artworkReactionList(filter: { sessionId: $sessionID }) {
      reaction
      playerCardId
      playerId
      sessionId
    }
  }
`;

/**
 * A state management function that provides session controls to the app
 * through the SessionContext that is accessible through the useSessionContext hook.
 */
function PlayerSessionControls({ sessionID }: { sessionID: string }) {
  // State
  const [currentPlayerId, setCurrentPlayerId] = useState<string | null>(null);
  const [state, setState] = useState<'initializing' | 'loading' | 'connected' | 'disconnected'>(
    'initializing'
  );
  const [session, setSession] = useState<API.SessionData>();
  const [artworkReactions, setArtworkReactions] = useState<Reaction[]>([]);

  const players = session?.players ?? [];
  // Prefer latest occurrence by id
  const currentPlayer = [...players].find((player) => player.id == currentPlayerId);

  const currentRound = session?.rounds?.find((round) => round?.isCurrent);
  const currentArtwork = currentRound?.artwork;

  useEffect(() => {
    try {
      const playerCookie = getCookie(`player-${sessionID}`);
      if (playerCookie) {
        const player = JSON.parse(playerCookie);
        if (player?.id) setCurrentPlayerId(player.id);
      }
    } catch (e) {
      console.log(e);
    }
  }, [currentPlayerId]);

  useQuery<GraphQLQuery<API.SessionGetQuery>>(sessionQuery, {
    variables: { sessionID },
    onCompleted: ({ sessionGet }) => {
      if (sessionGet) {
        setSession(sessionGet.data as API.SessionData);
        setState('connected');
      }
    },
    onError: () => {
      setState('disconnected');
    }
  });

  useSubscription<GraphQLSubscription<API.PlayerSessionUpdatedSubscription>>(playerSubscription, {
    variables: { id: sessionID },
    onSubscriptionData: ({ subscriptionData: { data } }) => {
      if (session && data?.playerSessionUpdated?.__typename === 'NewRoundUpdate') {
        if (!data.playerSessionUpdated) return;

        const { round, sessionStatus, playerStatus } = data.playerSessionUpdated;
        setSession({
          ...session,
          status: sessionStatus,
          rounds: session.rounds.map((sessionRound) => {
            if (sessionRound.index === round) {
              return {
                ...sessionRound,
                isCurrent: true
              };
            }

            return {
              ...sessionRound,
              isCurrent: false
            };
          }),
          players: session.players.map((player) => {
            return {
              ...player,
              gameStatus: playerStatus
            };
          })
        });
      }

      if (session && data?.playerSessionUpdated?.__typename === 'SharingPlayer') {
        if (!data.playerSessionUpdated) return;

        const { playerId } = data.playerSessionUpdated;

        setSession({
          ...session,
          status: API.SessionStatus.sharing,
          players: session.players.map((player) => {
            return {
              ...player,
              gameStatus:
                player.id === playerId
                  ? API.PlayerGameStatus.SHARING
                  : player.gameStatus === API.PlayerGameStatus.SHARING
                    ? API.PlayerGameStatus.SHARED
                    : player.gameStatus
            };
          })
        } as API.SessionData);
      }

      if (session && data?.playerSessionUpdated?.__typename === 'NewDrawingUpdate') {
        if (!data.playerSessionUpdated?.artwork) {
          return;
        }

        const artwork = {
          ...data.playerSessionUpdated.artwork,
          __typename: 'Artwork'
        } as API.Artwork;

        setSession({
          ...session,
          rounds: session.rounds.map((sessionRound) => {
            if (sessionRound.isCurrent) {
              return {
                ...sessionRound,
                artwork: [...sessionRound.artwork, artwork]
              };
            }

            return sessionRound;
          }),
          players: session.players.map((player) => {
            if (player.id === artwork.playerId) {
              return {
                ...player,
                gameStatus: API.PlayerGameStatus.SUBMITTED
              };
            }
            return player;
          })
        });
      }
      if (session && data?.playerSessionUpdated?.__typename === 'NewPlayerUpdate') {
        const updated = data.playerSessionUpdated.player as API.Player;
        const exists = session.players.some((p) => p.id === updated.id);
        setSession({
          ...session,
          players: exists
            ? session.players.map((p) => (p.id === updated.id ? { ...p, ...updated } : p))
            : [...session.players, updated]
        } as API.SessionData);
      }
      if (session && data?.playerSessionUpdated?.__typename === 'SessionStatusUpdate') {
        setSession({
          ...session,
          // cast to any to avoid codegen drift if schema updates are pending locally
          status: (data.playerSessionUpdated as any).status
        } as API.SessionData);
      }
    }
  });

  /**
   * Sets up a player for a session. This will create a player and set the player state.
   * @param name The name of the player.
   */
  const setupPlayer = async ({ name, feelingBefore }: { name: string; feelingBefore: number }) => {
    const playerSession = await playerJoinSession({ id: sessionID, name, feelingBefore });

    setSession(playerSession?.session.data as API.SessionData);

    const playerId = playerSession?.newPlayer.id as string;

    // adding player info to the cookie to reuse if player gets disconnected
    const expirationDate = new Date();
    expirationDate.setDate(expirationDate.getDate() + 1);
    setCookie(`player-${sessionID}`, JSON.stringify({ id: playerId }), {
      expires: expirationDate
    });
    setCurrentPlayerId(playerId);
  };

  /**
   * This will set the select player sentiment to the question.
   * @param sentiment The sentiment to update.
   */
  const playerUpdateSentiment = async (sentiment: number, isBefore: boolean) => {
    if (!currentPlayer?.id) return;
    await updateSentiment({
      id: sessionID,
      playerId: currentPlayer.id,
      sentiment,
      isBefore
    });
  };

  /**
   * This will set the select player answer to the question.
   * @param question The question to update.
   * @param answer The answer to the question.
   */
  const updatePlayerQuestion = async (question: string, answer: boolean) => {
    if (!currentPlayer?.id) return;
    await updateQuestion({ id: sessionID, playerId: currentPlayer.id, question, answer });
  };

  /**
   * Submits a drawing for the current prompt. This will create a player card
   * with the drawing.
   * @param drawing The drawing to submit as stringified JSON of the canvas.
   */
  const submitDrawing = async (drawing: string) => {
    if (!session) return;

    await playerSubmitDrawing({
      id: sessionID,
      playerId: currentPlayer?.id ?? '',
      drawing
    });
  };

  const reactToArtwork = async (reaction: React) => {
    if (!session || !currentPlayer) {
      return;
    }

    const sharingPlayerId = getCurrentSharingPlayer(players)?.id;

    const currentRound = session.rounds.find((round) => round.isCurrent);
    const currentArtworkId = currentRound?.artwork.find(
      (artwork) => artwork.playerId === sharingPlayerId
    )?.id as string;

    await playerReactToArtwork({
      reaction: reaction.value,
      playerId: currentPlayer.id,
      playerCardId: currentArtworkId,
      sessionId: sessionID
    });
  };

  const getCurrentSharingPlayer = (players: (API.Player | null)[]) => {
    return players.find(
      (sessionPlayer) => sessionPlayer?.gameStatus === API.PlayerGameStatus.SHARING
    );
  };

  /**
   * Gets the reactions for a specific artwork
   * @param artworkId The ID of the artwork to get reactions for
   * @returns Array of reactions for the specified artwork
   */
  const getArtworkReactions = async (): Promise<void> => {
    if (!session || !currentPlayer) {
      setArtworkReactions([]);
      return;
    }

    const sharingPlayerId = getCurrentSharingPlayer(players)?.id;

    const currentRound = session.rounds.find((round) => round.isCurrent);
    const currentArtworkId = currentRound?.artwork.find(
      (artwork) => artwork.playerId === sharingPlayerId
    )?.id as string;

    const reactions = await getReactions({
      playerId: currentPlayer.id,
      playerCardId: currentArtworkId,
      sessionId: sessionID
    });

    const convertedReactions: Reaction[] = reactions.map((reaction) => ({
      playerId: reaction.playerId,
      playerCardId: reaction.playerCardId,
      reactionType: reactionTypeForNumber(reaction.reaction),
      displayed: false
    }));

    setArtworkReactions(convertedReactions);
  };

  return {
    state,

    currentRound,
    artwork: currentArtwork ?? [],

    player: currentPlayer,
    players: session?.players ?? [],
    session,
    reactions: artworkReactions,

    setupPlayer: setupPlayer,
    submitDrawing: submitDrawing,
    updatePlayerQuestion: updatePlayerQuestion,
    updatePlayerSentiment: playerUpdateSentiment,
    reactToArtwork: reactToArtwork,
    getArtworkReactions,
    PlayerSessionProvider
  };
}

/**
 * Session provider component. This will provide the session context to all
 * child components of this component so that they can access the session state
 * after connecting to a session.
 * @param { SessionProps } props The props for the session provider.
 */
export default function PlayerSessionProvider({
  sessionID,
  children
}: {
  sessionID: string;
  children: ReactNode;
}) {
  const session = PlayerSessionControls({ sessionID });

  return <PlayerSessionContext.Provider value={session}>{children}</PlayerSessionContext.Provider>;
}
